/**
 * R3 · 改图工坊「能力卡 → 指令骨架」单源(设计稿 r2-remix-studio v2)。
 *
 * ═══ 一条纪律:指令框是唯一真相 ═══════════════════════════════════════════════
 * 能力卡**不是独立表单**,它只做一件事:把一段**确定性骨架**写进「改图指令」框,
 * 并高亮那一个需要用户填的变量。写完之后每个字都归用户 —— 可以整段重写、可以追加、
 * 也可以一张卡都不点从零自己写(= 原「图改图」的自由度一点没少)。
 *
 * 为什么不做成「卡片=一组表单字段」:那样等于把用户的表达能力限制在我们预设的槽位里,
 * 而 i2i 的效果高度依赖描述的具体程度 —— 用户比我们更清楚他要什么。卡只是**免于对着
 * 空框发呆**([[feedback_no_lazy_in_prompt_chain]]:提示词是请求不是保证,骨架也一样)。
 *
 * ★骨架里的语气全是**指令式**且**只描述要什么**,不写否定词
 *   (V28.3 教训:否定名词会被模型字面化反向触发)。
 */

/**
 * ★2026-07-31 删掉了 `fidelity`(原 "strict" | "loose" | "free")。
 *
 * 它是个**死字段**:全仓 grep 只有定义与自测,`RemixRecipePicker` 不渲染它、
 * `applyRecipe` 不读它、提交 payload 也不带它 —— 三张卡上写着"推荐保真档",
 * 实际一档都没生效过。留着一个不生效的档位比没有更糟(它让人以为保真度可调,
 * 于是「做个类似的」出来一模一样时,没人会怀疑到"根本没有这个开关"上去)。
 *
 * 真正让用户拿到"类似但不同"的,是下面新增的 `recreate` 卡 —— 它换的是**结构**
 * (不把原图当 edit base),不是一个强度旋钮。万相 i2i 本来也没有强度参数
 * (`wanxiang.py` 只吃 ref_images + prompt),所以那个旋钮即使接上也无处可接。
 */
export interface RemixRecipe {
  key: string;
  /** 卡面标题(动词开头,说清"对这张图做什么")。 */
  title: string;
  /** 一句话说清结果,不说实现。 */
  desc: string;
  /** 需要用户填的那一个变量的提示(卡选中后聚焦到指令框里这一段)。 */
  variableHint: string;
  /**
   * true = 这张卡的变量**不是用户手填的**,而是先让 AI 看一眼参考图写出来
   * (`posterService.describeReference`),再落进指令框给用户过目/改写。
   *
   * ★为什么要有这个标记:带它的卡走的是**文生图**(参考图只贡献"长什么样"的信息,
   * 不再当 edit base),点它必须同时把模式切回文字 —— 这是 `applyRecipe` 的分支依据,
   * 不是展示用的。
   */
  vision?: boolean;
  /**
   * 指令骨架。`{{X}}` 是唯一占位:落进指令框时替换成 `variableHint` 的示例文字,
   * 用户直接改那一段即可。
   */
  skeleton: string;
}

export const REMIX_RECIPES: readonly RemixRecipe[] = [
  {
    key: "bg",
    title: "换背景",
    desc: "商品抠出来,背景换成你描述的场景;商品本身不动。",
    variableHint: "新背景长什么样",
    skeleton:
      "保留画面里的主体商品:轮廓、材质、文字与光泽全部维持原样。把背景替换为:{{X}}。" +
      "背景的透视与光线方向要与商品原本的投影一致,商品边缘自然融入新场景。",
  },
  {
    key: "restyle",
    title: "风格重绘",
    desc: "构图不变,整张换一种画风。",
    variableHint: "想要的画风",
    skeleton:
      "保持原图的构图与元素位置(主体、文字块、留白关系都在原处),把整张图的视觉风格改为:{{X}}。" +
      "颜色、材质、光影按新风格统一处理,文字仍然清晰可读。",
  },
  {
    key: "retext",
    title: "保构图改字",
    desc: "标题 / 价格 / 活动时间换掉,版式与画面尽量保持。",
    variableHint: "新的文案内容(逐行写:标题、副标、价格…)",
    skeleton:
      "保持原图的版式、配色与画面元素不变,只替换其中的文字内容为:{{X}}。" +
      "新文字沿用原有的字号层级与位置,长度不同时在原文字块范围内自适应排版。",
  },
  {
    key: "recreate",
    title: "照这张再做一版",
    desc: "参考它的风格和题材,重新构图做一张新的 —— 不是在原图上改。",
    variableHint: "AI 正在看这张图…",
    vision: true,
    skeleton: "{{X}}",
  },
];

/** 卡 → 指令框初始文本(把占位换成给用户改的示例段落)。 */
export function skeletonFor(recipe: RemixRecipe, variable?: string): string {
  return recipe.skeleton.replace("{{X}}", variable?.trim() || `〈${recipe.variableHint}〉`);
}

/**
 * 指令是否还停在**我们生成的**待填段上(提交前拦一道,与长图「还没填画面描述」同口径)。
 *
 * ★判据必须是「恰好等于某张卡的 variableHint」,不能是"含有任意〈…〉"(codex R3):
 * 用户完全可能自己写「把文案改成〈夏日特惠〉」—— 那是他的内容,不是我们的占位,
 * 拦下来就是把正常请求判成没填完。同款教训见长图占位:判据落在**字面量**上,不落在形状上。
 */
const SLOT_LITERALS = new Set(REMIX_RECIPES.map((r) => `〈${r.variableHint}〉`));

export function hasUnfilledSlot(text: string): boolean {
  return [...SLOT_LITERALS].some((slot) => text.includes(slot));
}

/**
 * 点卡时**必须同时复位**的表单字段(codex R5)。
 *
 * `handleGenerate` 在 `isAiOptimized` 为真时用 `contentDescription/systemConstraints`
 * 拼 rawPrompt,**完全不读 prompt** —— 先在文字 tab 用过 AI 优化、再切改图点卡,
 * 提交上去的是上一条海报的优化文案,卡片指令一个字都到不了引擎(界面 A 成片 B)。
 * 抽成常量是为了让单测能锁住"漏清一个都算错",而不是散在组件里靠人记。
 */
export const RECIPE_RESET_FIELDS = [
  "isAiOptimized",
  "contentDescription",
  "systemConstraints",
  "originalPromptSnapshot",
] as const;
